import { Box, Paper, useMediaQuery } from "@material-ui/core";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import LineChart from "src/components/ScoringChart/Line/LineChart";
import { beautifulTooltip } from "src/components/ScoringChart/tool-utils/BeautifulTooltip";
import DateGroupButton, { DATE_OPTION } from "src/components/ScoringChart/tool-utils/DateGroupButton";
import { NoteTypography } from "src/components/Typography/TitleTypography";
import DetailMonitoringWrapper from "src/components/Wrapper/DetailMonitoringWrapper";
import { MONITORING } from "src/config/constants";
import { useUserAnalysisChartSelector, useUserAnalysisSelector } from "src/context/monitoring/userAnalysisContext";

const getOption = () => {
  return {
    chart: {
      height: 500,
    },
    xAxis: {
      gridLineWidth: 1,
      type: "datetime",
      labels: {
        enabled: true,
        format: "{value:%b/%e/%y}",
      },
    },
    legend: {
      enabled: true,
    },
    ...beautifulTooltip((value) => value),
  };
};

export default function UserActiveness() {
  const { pool } = useParams();
  const match = useMediaQuery((theme) => theme.breakpoints.only("xs"));
  const { fetchUserActiveness } = useUserAnalysisSelector((state) => state.fetch);
  const userActiveness = useUserAnalysisChartSelector(MONITORING.ANALYSIS.userActiveness, pool);

  useEffect(() => {
    if (userActiveness?.active === undefined) fetchUserActiveness(pool, { number: 360, interval: 2 }, { active: 2 });
    else fetchUserActiveness(pool, userActiveness?.metadata, { active: userActiveness?.active });
  }, [userActiveness?.active, pool]);

  const { series, isLoading } = useMemo(() => {
    if (userActiveness) {
      return {
        series: [
          {
            name: "Active Users",
            data: userActiveness.activeUsers,
          },
          {
            name: "Borrowing Users",
            data: userActiveness.borrowingUsers,
          },
          {
            name: "Deposit-only users",
            data: userActiveness.depositOnlyUsers,
          },
        ],
        isLoading: false,
      };
    }
    return {
      series: [
        {
          name: "Active Users",
          data: [],
        },
        {
          name: "Borrowing Users",
          data: [],
        },
        {
          name: "Deposit-only users",
          data: [],
        },
      ],
      isLoading: true,
    };
  }, [userActiveness]);

  return (
    <DetailMonitoringWrapper
      navigation={{ text: "user analysis", name: "user activeness", link: `/user-analysis/${pool}` }}
      selectLink="/user-analysis/user-activeness"
      Component={{
        Description: (
          <NoteTypography>
            The chart shows the number of active users, deposit-only users and borrowing users over time. Such data
            indicates the market size and the attractiveness of a lending pool. For example, the number of active users
            gradually increasing over time implies that users put their trust in the lending pool. Similarly, the large
            number of deposit-only users shows that the lending pool is reputable enough for users to deposit their
            money in the long term.
          </NoteTypography>
        ),
      }}
    >
      <Paper>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <DateGroupButton
            fullWidth={match}
            defaultActive={userActiveness?.active ?? 0}
            picker={{
              continueClick: (from, to, active) => {
                fetchUserActiveness(pool, { from, to }, { active });
              },
            }}
            config={DATE_OPTION.map((item, _) => ({
              data: item,
              onClick: (data, active) => {
                fetchUserActiveness(pool, { number: data.number, interval: data.interval }, { active });
              },
            }))}
          />
        </Box>
        <LineChart isLoading={isLoading} series={series} option={getOption()} />
      </Paper>
    </DetailMonitoringWrapper>
  );
}
